import {
  Avatar,
  AvatarFallback,
} from "@arcadeai/design-system/components/ui/atoms/avatar";
import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import type { ColumnDef } from "@tanstack/react-table";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { PROJECT } from "@/app/capabilities/permissions";
import type { ProjectMember } from "@/app/members/types";
import { RoleBadge } from "@/app/roles/components/role-badge";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { useSession } from "@/lib/auth-client";
import { getInitials } from "@/lib/utils";
import { DeleteMemberButton } from "./delete-member-button";
import { MemberRoleSwitcher } from "./member-role-switcher";

const MemberNameCell = ({ member }: { member: ProjectMember }) => {
  const { data: session } = useSession();
  const isCurrentUser = session?.user.accountId === member.account_id;
  const displayName = member.preferred_name ?? member.full_name;

  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-8 w-8 border">
        <AvatarFallback className="text-xs">
          {getInitials(displayName)}
        </AvatarFallback>
      </Avatar>
      <span className={isCurrentUser ? "font-semibold" : "font-medium"}>
        {displayName}
      </span>
      {isCurrentUser && <Badge variant="default">You</Badge>}
    </div>
  );
};

const MemberRoleCell = ({ member }: { member: ProjectMember }) => {
  const { canProject } = useProjectPermissions();
  const { role } = member;

  if (!role) {
    return null;
  }

  const canEdit =
    canProject(PROJECT.members.update) &&
    member.actions.can_change_role === true;

  return canEdit ? (
    <MemberRoleSwitcher member={{ ...member, role }} />
  ) : (
    <RoleBadge role={role} />
  );
};

export const getProjectMembersColumns = (
  showRole: boolean,
  canDelete: boolean
): ColumnDef<ProjectMember>[] => [
  {
    accessorKey: "preferred_name",
    header: "Name",
    cell: ({ row }) => <MemberNameCell member={row.original} />,
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => (
      <span className="text-muted-foreground">{row.original.email}</span>
    ),
  },
  ...(showRole
    ? [
        {
          id: "role",
          accessorFn: (row: ProjectMember) => row.role?.display_name ?? "",
          meta: {
            displayName: "Role",
            defaultVisible: true,
          },
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Role" />
          ),
          cell: ({ row }) => <MemberRoleCell member={row.original} />,
        } satisfies ColumnDef<ProjectMember>,
      ]
    : []),
  // Remove is the only row action, so omit the column entirely when the caller
  // cannot delete members rather than render an empty column.
  ...(canDelete
    ? [
        {
          id: "actions",
          meta: {
            displayName: "Actions",
            defaultVisible: true,
          },
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Actions" />
          ),
          cell: ({ row }) => {
            const member = row.original;

            return (
              <DeleteMemberButton
                cannotRemoveReason={member.actions.cannot_remove_reason}
                canRemove={member.actions.can_remove}
                memberEmail={member.email}
                memberId={member.account_id}
              />
            );
          },
        } satisfies ColumnDef<ProjectMember>,
      ]
    : []),
];
